import { createRef, useState } from 'react';

import { CellInput, Container } from './MFACodeInput.styles';

function replaceAt(str: string, position: number, value: string): string {
  return str.substring(0, position) + value + str.substring(position + 1);
}

export interface MFACodeInputProps {
  code: string;
  codeLength: number;
  onChange: (value: string) => unknown;
  disabled?: boolean;
  hasError?: boolean;
}

export function MFACodeInput({ code, codeLength, onChange, disabled, hasError }: MFACodeInputProps) {
  const [inputRefs] = useState(() => [...Array(codeLength)].map(() => createRef<HTMLInputElement>()));

  const focusNextInput = (index: number) => {
    if (index + 1 >= inputRefs.length) return;

    inputRefs[index + 1].current?.focus();
  };

  const focusPrevInput = (index: number) => {
    if (index - 1 < 0) return;

    inputRefs[index - 1].current?.focus();
  };

  const handleInputChange = (e: React.ChangeEvent<HTMLInputElement>, inputIndex: number) => {
    const inputValue = e.target.value;

    if (code.length === codeLength && inputValue !== '') return;

    let newCode = code;

    if (inputIndex >= code.length) {
      newCode = code + inputValue;
      inputIndex = newCode.length - 1;
    } else {
      newCode = replaceAt(code, inputIndex, inputValue);
    }

    onChange(newCode);

    if (inputValue !== '') {
      focusNextInput(inputIndex);
    }
  };

  const handleInputKeyPress = (e: React.KeyboardEvent<HTMLInputElement>, inputIndex: number) => {
    if (e.key === 'Backspace') {
      requestAnimationFrame(() => {
        focusPrevInput(inputIndex);
      });
    }
  };

  const handleInputPaste = (e: React.ClipboardEvent<HTMLInputElement>) => {
    const clipboardData = e.clipboardData.getData('text').trim();

    onChange(clipboardData.slice(0, codeLength));

    e.preventDefault();
  };

  return (
    <Container aria-label="mfa code input" aria-disabled={disabled}>
      {[...Array(codeLength)].map((_, i) => (
        <CellInput
          key={i}
          ref={inputRefs[i]}
          aria-label="MFA code input"
          value={code[i] || ''}
          onChange={(e) => handleInputChange(e, i)}
          onKeyDown={(e) => handleInputKeyPress(e, i)}
          onPaste={(e) => handleInputPaste(e)}
          $hasError={hasError}
        />
      ))}
    </Container>
  );
}
